import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import { StatusBadge, PriorityBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import { listMyAssignedTickets } from "../../api/myAssignedTicketService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { fromNow, ticketNumber, serialNumber } from "../../utils/formatters";

const FILTERS = [
  { id: "all", label: "All", params: {} },
  { id: "open", label: "Open", params: { status: "open" } },
  { id: "in_progress", label: "In progress", params: { status: "in_progress" } },
  { id: "resolved", label: "Resolved", params: { status: "resolved" } },
  { id: "closed", label: "Closed", params: { status: "closed" } },
];

export default function MyAssignedTicketsPage() {
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [priority, setPriority] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [filter, debouncedSearch, priority, perPage]);

  const tickets = useApi(
    () => {
      const params = { page, per_page: perPage, ...FILTERS.find((f) => f.id === filter).params };
      if (priority) params.priority = priority;
      if (debouncedSearch) params.search = debouncedSearch;
      return listMyAssignedTickets(params);
    },
    [page, perPage, filter, priority, debouncedSearch, refreshKey],
  );
  const rows = paginatedRows(tickets.data);
  const meta = paginatedMeta(tickets.data);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Assigned Tickets</h1>
          <div className="page-sub">{meta?.total ?? rows.length} tickets in this view</div>
        </div>
      </div>

      <div className="filter-bar">
        {FILTERS.map((f) => (
          <button key={f.id} className={`chip ${filter === f.id ? "active" : ""}`} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
        <select className="select" style={{ width: 160 }} value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="">Any priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon name="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }} />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Subject, description or customer name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {tickets.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : rows.length === 0 ? (
            <EmptyState icon="message-circle" title="No tickets in this view" />
          ) : (
            <table className="table">
              <thead><tr><th style={{ width: 50 }} className="t-num">#</th><th style={{ width: 80 }}>Ticket</th><th>Subject</th><th>Customer</th><th>Priority</th><th>Status</th><th>Last update</th><th style={{ width: 70 }}>Actions</th></tr></thead>
              <tbody>
                {rows.map((t, i) => (
                  <tr key={t.id} className="clickable" onClick={() => navigate(`/me/tickets/${t.id}`)}>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td className="t-mono t-muted">{ticketNumber(t.id)}</td>
                    <td><div className="t-primary">{t.subject}</div></td>
                    <td className="t-muted">{t.customer?.name}</td>
                    <td><PriorityBadge priority={t.priority} /></td>
                    <td><StatusBadge status={t.status} /></td>
                    <td className="t-muted">{fromNow(t.updated_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="icon-btn"
                        title="View"
                        onClick={() => navigate(`/me/tickets/${t.id}`)}
                      >
                        <Icon name="eye" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={tickets.loading}
          />
        </div>
      </div>
    </>
  );
}
